<template>
  <mf-loading-dialog :loading="loadingDialog">
    <base-page-layout title="Mensagens" class="novo-template-mensagens">
      <v-card class="py-4 px-6">
        <v-row>
          <v-col cols="6">
            <h1>Novo modelo de mensagem</h1>
            <p class="mt-3 mb-5">Esse modelo vai ser guardado e vai poder ser utilizado novamente por você ou qualquer outro usuário da sua plataforma.</p>
            <h3>Selecione os canais onde essa mensagem pode ser utilizada</h3>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4" class="py-0">
            <v-container fluic class="pa-0">
              <v-row>
                <v-col v-for="(channel, index) in channels" :key="index" class="py-0" cols="6">
                  <v-checkbox
                    v-model="channel.active"
                    hide-details
                    single-line
                    color="primary"
                    :disabled="!hasPermission(channel.name)"
                    :error="$v.selectedChannels.$error"
                    :label="channel.label"
                  />
                </v-col>
              </v-row>
              <div v-if="$v.selectedChannels.$error" class="v-messages theme--light error--text mt-1" role="alert">Selecione ao menos um canal</div>
            </v-container>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="6">
            <v-container fluic class="pa-0">
              <v-row>
                <h3 class="px-3">Selecione os marcadores aos quais essa mensagem pertence</h3>
                <v-col cols="12">
                  <mf-search v-model.lazy="search" placeholder="Buscar por marcadores" hide-details />
                </v-col>
              </v-row>
              <p v-if="filterTags.length === 0" class="no-result">Nenhum resultado encontrado</p>
              <v-row v-else no-gutters>
                <v-col>
                  <v-row no-gutters>
                    <v-col :class="[showAll ? 'flex-wrap pb-2' : 'tag-list-less pb-2']">
                      <span
                        v-for="(tag, index) in filterTags"
                        :key="index"
                        class="tag mr-2 mt-2"
                        :style="`background-color: ${tag.color};`"
                        @click="addTagToFilter(tag)"
                        >{{ tag.name }}</span
                      >
                    </v-col>
                  </v-row>
                  <v-row v-if="filterTags.length > 2" justify="end" no-gutters>
                    <span class="link" @click="showAll = !showAll">
                      {{ showAll ? 'Esconder' : 'Mostrar todos' }}
                    </span>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-if="selectedTags && selectedTags.length" class="mt-3" no-gutters>
                <h3>Marcadores selecionados:</h3>
              </v-row>
              <v-row v-if="selectedTags && selectedTags.length" no-gutters>
                <span
                  v-for="(tag, index) of selectedTags"
                  :key="index"
                  class="tag mr-2 mt-2"
                  :style="`background-color: ${tag.color};`"
                  @click="removeTagFromFilter(index)"
                >
                  {{ tag.name }}
                  <mf-icon x-small>close</mf-icon>
                </span>
              </v-row>
              <v-row v-if="selectedTags.length !== 0" class="mt-1" no-gutters>
                <v-col>
                  <span class="link" @click="removeAll()"> Remover todos </span>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="6">
            <div class="d-flex justify-space-between mb-2">
              <h3>Escreva aqui sua mensagem:</h3>
              <select-campaigns
                :selected.sync="campaigns"
                :status="['ACTIVE', 'FINISHED', 'SCHEDULED']"
                :mode="mode"
                show-message
                label="Importar de campanha"
                type="text"
                single-line
                single-checkbox
                default-table-sort-by="validity"
                :default-table-sort-desc="true"
              />
            </div>

            <mf-textarea-emoji
              v-model="value"
              class="message-textarea primary-text"
              hide-details
              outlined
              no-resize
              hide-counter
              auto-grow
              rows="10"
              placeholder="Texto da mensagem"
              :error="$v.value.$error"
              @input="handleTextarea"
            />

            <v-layout class="input-details d-flex justify-end">
              <div v-if="$v.value.$error" class="v-messages theme--light error--text" role="alert">Crie uma mensagem para este modelo</div>
              <span class="right-text">{{ charsUsed }}</span>
            </v-layout>
          </v-col>
          <v-col cols="6">
            <h3>Você pode incluir variáveis no seu modelo de mensagem:</h3>
            <div v-for="(variable, index) in variables" :key="index" class="chips-container d-flex">
              <div class="chip-item">
                <v-chip v-clipboard="() => variable.clipboardText" color="cinza-l90" @success="handleSuccess">
                  <span class="text-color">{{ variable.text }}</span>
                  <mf-icon right size="20" class="secondary-text ml-1">content_copy</mf-icon>
                </v-chip>
              </div>
              <div class="mt-n1 text-container">
                <span class="column-title">{{ variable.description }}</span>
                <p class="subtitle mt-1" style="margin: 0">Contabiliza {{ variable.charQuantity }} dígitos</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <mf-action-buttons
        class="mt-2"
        :primary-button="{
          text: 'Salvar',
          action: save,
          isVisible: true,
          isLoading: loading,
        }"
        :cancel-button="{
          text: 'Cancelar',
          action: () => $router.push({ path: '/configuracoes/mensagens' }),
          isVisible: true,
          isDisabled: false,
          isLoading: loading,
        }"
      />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import { MUTATION_CREATE_UPDATE_MESSAGE, QUERY_GET_TAGS, QUERY_GET_MESSAGE } from '@/modules/configuracoes/template-mensagens/graphql'
import { required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import SelectCampaigns from '@/components/select-campaigns/SelectCampaigns.vue'
import { characterVariables } from '@/mixins/character-variables'
export default {
  name: 'CreateUpdateMessageTemplate',
  components: {
    BasePageLayout: () => import('@/components/BasePageLayout.vue'),
    SelectCampaigns,
  },
  mixins: [characterVariables],
  data: () => ({
    loadingDialog: false,
    loading: false,
    id: '',
    value: '',
    showAll: false,
    search: '',
    channels: [
      {
        name: 'PUSH',
        label: 'Push',
        active: false,
      },
      {
        name: 'WHATSAPP',
        label: 'Whatsapp',
        active: false,
      },
      {
        name: 'SMS',
        label: 'SMS',
        active: false,
      },
      {
        name: 'EMAIL',
        label: 'E-mail',
        active: false,
      },
    ],
    allTags: [],
    filterTags: [],
    selectedTags: [],
    campaigns: [],
    from_campaign: '',
  }),
  computed: {
    selectedChannels() {
      return this.channels.filter((channel) => channel.active).map((channel) => channel.name)
    },
    mode() {
      let channels = []
      if (this.$permissions.havePagePermission('campanhas/sms')) channels.push('SMS')
      if (this.$permissions.havePagePermission('campanhas/email-v2') && this.$getAllowedModules()['email_v2']) channels.push('EMAIL')
      if (this.$permissions.havePagePermission('campanhas/whatsapp') && this.$getAllowedModules()['whatsapp']) channels.push('WHATSAPP')
      if (this.$permissions.havePagePermission('campanhas/aplicativo') && this.$getAllowedModules()['push']) channels.push('PUSH')
      return channels
    },
    queryVariables() {
      const tags = this.selectedTags.map((tag) => tag._id)
      let variables = {
        message: this.value,
        tags,
        channels: this.selectedChannels,
      }
      if (this.from_campaign) variables = { ...variables, from_campaign: this.from_campaign }
      if (this.id) variables = { ...variables, _id: this.id }

      return variables
    },
  },
  watch: {
    search(search) {
      if (!this.search.trim()) this.filterTags = [...this.allTags]
      else this.filterTags = this.allTags.filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()))
    },
    campaigns: {
      deep: true,
      handler() {
        const { message, _id } = this.campaigns[0]
        this.from_campaign = _id
        this.value = message.text
        this.textLocal = this.value
        this.updateCharsUsedLength()
      },
    },
  },

  async mounted() {
    if (this.$route.params.id) await this.fetchMessageTemplate(this.$route.params.id)
    await this.fetchMessageTags()
    this.filterTags = [...this.allTags]
    this.updateCharsUsedLength()
  },
  methods: {
    hasPermission(channel) {
      if (channel === 'SMS') return this.$permissions.havePagePermission('campanhas/sms')
      if (channel === 'PUSH') return this.$permissions.havePagePermission('campanhas/aplicativo') && this.$getAllowedModules()['push']
      if (channel === 'WHATSAPP') return this.$permissions.havePagePermission('campanhas/whatsapp') && this.$getAllowedModules()['whatsapp']
      if (channel === 'EMAIL') return this.$permissions.havePagePermission('campanhas/email-v2') && this.$getAllowedModules()['email_v2']
    },
    handleTextarea() {
      this.textLocal = this.value
      this.updateCharsUsedLength()
    },
    addTagToFilter(tag) {
      const currentAdded = this.selectedTags.find((item) => item.name === tag.name)
      if (currentAdded) return
      this.selectedTags.push(tag)
      this.search = ''
    },
    removeTagFromFilter(index) {
      this.selectedTags.splice(index, 1)
    },
    removeAll() {
      this.search = ''
      this.filterTags = this.allTags
      this.selectedTags = []
    },
    handleSuccess(e) {
      this.$snackbar({ message: 'Copiado para área de transferencia', snackbarColor: 'info' })
    },
    async save() {
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente.', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
        return
      }
      try {
        this.loading = true
        await this.$apollo.mutate({
          mutation: MUTATION_CREATE_UPDATE_MESSAGE,
          variables: {
            data: this.queryVariables,
          },
        })
        const message = this.id ? 'Modelo atualizado com sucesso!' : 'Modelo criado com sucesso!'
        this.$snackbar({ message, snackbarColor: 'success' })
        this.$router.push({ path: '/configuracoes/mensagens' })
      } catch {
        this.$snackbar({ message: 'Ocorreu um erro, tente novamente mais tarde.', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    async fetchMessageTags() {
      try {
        this.loadingDialog = true
        const {
          data: {
            messageTemplateTags: { tags },
          },
        } = await this.$apollo.query({
          query: QUERY_GET_TAGS,
          fetchPolicy: 'no-cache',
        })
        this.allTags = tags
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao carregar as tags de mensagem.' })
        console.log(e)
      } finally {
        this.loadingDialog = false
      }
    },
    async fetchMessageTemplate(_id) {
      try {
        this.loadingDialog = true
        const {
          data: { messageTemplate },
        } = await this.$apollo.query({
          query: QUERY_GET_MESSAGE,
          fetchPolicy: 'no-cache',
          variables: { _id },
        })

        this.id = _id
        this.value = messageTemplate?.message
        this.selectedTags = messageTemplate?.tags.filter((tag) => tag)
        this.channels = this.channels?.map((channel) => {
          return {
            ...channel,
            active: messageTemplate.channels.includes(channel.name),
          }
        })
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao carregar o modelo de mensagem.' })
        console.log(e)
      } finally {
        this.loadingDialog = false
      }
    },
  },
  validations() {
    return {
      value: { required, notOnlySpace },
      selectedChannels: { required },
    }
  },
}
</script>

<style lang="scss" scoped>
.novo-template-mensagens {
  & h1 {
    margin: 0;
    font-weight: bold;
    font-size: 26px;
    line-height: 120%;
    color: $cinza-d10;
  }

  & p,
  h3 {
    font-size: 16px;
    line-height: 120%;
    color: $cinza-0;
    margin: 0;
  }

  & h3 {
    color: $cinza-d10;
    font-weight: bold;
  }

  & .no-result {
    font-size: 14px;
  }

  .tag-list-less {
    display: flex;
    flex-wrap: initial;
    overflow: hidden;
    white-space: nowrap;
  }

  .flex-wrap {
    display: flex;
    flex-wrap: inherit;
  }

  ::v-deep .v-textarea.v-text-field--enclosed .v-text-field__slot textarea {
    font-family: 'Roboto Mono', monospace;
    line-height: 32px;
    margin-top: 8px;
  }
  .message-textarea {
    .v-input__slot {
      background-color: $branco !important;
      border-color: #868e96 !important;
    }
  }

  .subtitle {
    font-size: 14px;
  }

  .chips-container {
    padding: 10px 0;
    gap: 10px;
  }
  .chip-item {
    width: 170px;
  }
  .text-container {
    width: calc(100% - 170px);
    & span {
      line-height: 110%;
      display: block;
    }
  }
}
</style>
